import { BaseFeatureFlagsService } from './BaseFeatureFlagsService';
import type { FeatureFlagsService } from './FeatureFlagsService';

interface FeatureFlagsConfig<FeatureFlag extends string> {
  enabledFeatureFlags: Array<FeatureFlag>;
}
export class LocalStorageFeatureFlagsService<FeatureFlag extends string>
  extends BaseFeatureFlagsService<FeatureFlag>
  implements FeatureFlagsService<FeatureFlag>
{
  private localStorage: Storage;
  private location: Location;
  private allFeatureFlags: ReadonlyArray<FeatureFlag>;

  constructor(
    localStorage: Storage,
    location: Location,
    allFeatureFlags: ReadonlyArray<FeatureFlag>,
  ) {
    super();
    this.localStorage = localStorage;
    this.location = location;
    this.allFeatureFlags = allFeatureFlags;
  }

  get(featureFlag: FeatureFlag) {
    return [
      ...this.fromLocalConfig().enabledFeatureFlags,
      ...this.fromUrl().enabledFeatureFlags,
    ].includes(featureFlag.toLocaleLowerCase() as FeatureFlag);
  }

  override set(name: FeatureFlag, value: boolean) {
    super.set(name, value);
    const enabledFeatureFlags = [
      ...this.fromLocalConfig().enabledFeatureFlags.filter(
        featureFlag => featureFlag !== name,
      ),
      ...(value ? [name] : []),
    ];
    this.toLocalStorage({ enabledFeatureFlags });
  }

  private fromUrl(): FeatureFlagsConfig<FeatureFlag> {
    const urlParams = new URLSearchParams(this.location.search);
    const enabledFeatureFlagsFromUrl =
      urlParams.get('enabledFeatureFlags')?.split(',') ?? [];

    if (urlParams.get('enableAllFF') === 'true') {
      const featureFlags = {
        enabledFeatureFlags: Object.values(this.allFeatureFlags).map(
          featureFlag => featureFlag.toLowerCase() as FeatureFlag,
        ),
      };
      this.toLocalStorage(featureFlags);
      return featureFlags;
    }

    return {
      enabledFeatureFlags: enabledFeatureFlagsFromUrl.map(
        featureFlag => featureFlag.toLowerCase() as FeatureFlag,
      ),
    };
  }

  private fromLocalConfig(): FeatureFlagsConfig<FeatureFlag> {
    const featureFlagsConfig = this.localStorage.getItem('featureFlags');
    if (!featureFlagsConfig) return { enabledFeatureFlags: [] };
    try {
      const config = JSON.parse(featureFlagsConfig);
      return {
        enabledFeatureFlags: config.enabledFeatureFlags.map(
          (featureFlag: string) => featureFlag.toLowerCase() as FeatureFlag,
        ),
      };
    } catch {
      return { enabledFeatureFlags: [] };
    }
  }

  private toLocalStorage(config: FeatureFlagsConfig<FeatureFlag>) {
    try {
      this.localStorage.setItem('featureFlags', JSON.stringify(config));
    } catch {}
  }
}
